import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Form, DatePicker, Select, Button, Space, Table, Tag, Segmented, Drawer,
} from 'antd';
import { TeamOutlined, CalendarOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../api';
import ResizableTitle from '../components/ResizableTitle';

import { selectFilter } from '../utils/selectFilter';
const { RangePicker } = DatePicker;

const STATUS_COLOR = {
  assigned:          'blue',
  in_progress:       'orange',
  completed:         'green',
  rescore_requested: 'purple',
  canceled:          'default',
};

const STATUS_LABEL = {
  assigned:          'Draft',
  in_progress:       'In Progress',
  completed:         'Completed',
  rescore_requested: 'Disputed',
  canceled:          'Canceled',
};

const METRIC_COLS = [
  {
    title: 'Qty Submissions Pending',
    dataIndex: 'PendingCount',
    key: 'PendingCount',
    width: 180,
    align: 'center',
    sorter: (a, b) => a.PendingCount - b.PendingCount,
    render: (v) => v > 0 ? <Tag color="blue">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Qty Submissions Disputed',
    dataIndex: 'DisputedCount',
    key: 'DisputedCount',
    width: 185,
    align: 'center',
    sorter: (a, b) => a.DisputedCount - b.DisputedCount,
    render: (v) => v > 0 ? <Tag color="purple">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Qty Submissions Completed',
    dataIndex: 'CompletedCount',
    key: 'CompletedCount',
    width: 195,
    align: 'center',
    sorter: (a, b) => a.CompletedCount - b.CompletedCount,
    render: (v) => v > 0 ? <Tag color="green">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Qty Section Errors',
    dataIndex: 'TotalErrors',
    key: 'TotalErrors',
    width: 145,
    align: 'right',
    sorter: (a, b) => a.TotalErrors - b.TotalErrors,
    render: (v) => v > 0 ? <Tag color="red">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Qty Section Feedbacks',
    dataIndex: 'TotalFeedbacks',
    key: 'TotalFeedbacks',
    width: 160,
    align: 'right',
    sorter: (a, b) => a.TotalFeedbacks - b.TotalFeedbacks,
    render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
];

const DRILL_COLS = (navigate) => [
  {
    title: 'Status',
    dataIndex: 'Status',
    key: 'Status',
    width: 120,
    render: (s) => <Tag color={STATUS_COLOR[s] ?? 'default'}>{STATUS_LABEL[s] ?? s}</Tag>,
  },
  { title: 'Date of Error', dataIndex: 'DateOfError',   key: 'DateOfError',   width: 115 },
  { title: 'Agent',         dataIndex: 'AgentName',     key: 'AgentName',     width: 160, ellipsis: true },
  { title: 'Campaign',      dataIndex: 'Campaign_Name', key: 'Campaign_Name', width: 160, ellipsis: true },
  { title: 'Form',          dataIndex: 'FormName',      key: 'FormName',      ellipsis: true },
  {
    title: 'Errors',
    dataIndex: 'TotalErrors',
    key: 'TotalErrors',
    width: 90,
    align: 'right',
    render: (v) => v > 0 ? <Tag color="red">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Feedbacks',
    dataIndex: 'TotalFeedbacks',
    key: 'TotalFeedbacks',
    width: 100,
    align: 'right',
    render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: '',
    key: 'view',
    width: 60,
    render: (_, r) => (
      <Button size="small" type="link" onClick={(e) => { e.stopPropagation(); navigate(`/errors/${r.ID}`); }}>
        View
      </Button>
    ),
  },
];

export default function ErrorEvaluatorStatsPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [forms, setForms]           = useState([]);
  const [campaigns, setCampaigns]   = useState([]);
  const [viewMode, setViewMode]     = useState('evaluator');
  const [colWidths, setColWidths]   = useState({});
  const [drillOpen, setDrillOpen]   = useState(false);
  const [drillTitle, setDrillTitle] = useState('');
  const [drillRows, setDrillRows]   = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const toResizable = (col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 120,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  });

  useEffect(() => {
    api.get('/api/errors/filter-options')
      .then((r) => {
        setCampaigns(r.data.campaigns.map((c) => ({ label: c.Name, value: c.ID })));
        setForms(r.data.forms.map((f) => ({ label: f.name, value: f.name })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    form.setFieldsValue({ dateRange: [dayjs().startOf('month'), dayjs()] });
  }, []); // eslint-disable-line

  const buildParams = useCallback((extra = {}) => {
    const values = form.getFieldsValue();
    const p = { ...extra };
    if (values.dateRange?.[0])   p.start_date = values.dateRange[0].format('YYYY-MM-DD');
    if (values.dateRange?.[1])   p.end_date   = values.dateRange[1].format('YYYY-MM-DD');
    if (values.campaigns?.length) p.campaigns  = values.campaigns.join(',');
    if (values.form_id)           p.form_name  = values.form_id;
    return p;
  }, [form]);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/errors/evaluator-stats', {
        params: buildParams({ group_by: viewMode }),
      });
      setRows(res.data);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [buildParams, viewMode]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const openDrill = useCallback(async (record) => {
    const label = record.Month
      ? `${record.EvaluatorName} — ${record.Month}`
      : record.EvaluatorName;
    setDrillTitle(label);
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillRows([]);
    try {
      const p = buildParams({ evaluator_user_id: record.AssignedToUserID });
      if (record.MonthKey) p.month_key = record.MonthKey;
      const res = await api.get('/api/errors/evaluator-stats/drill', { params: p });
      setDrillRows(res.data);
    } catch {
      setDrillRows([]);
    } finally {
      setDrillLoading(false);
    }
  }, [buildParams]);

  // ── Columns ───────────────────────────────────────────────────────────────
  const evaluatorCol = {
    title: 'Evaluator',
    dataIndex: 'EvaluatorName',
    key: 'EvaluatorName',
    sorter: (a, b) => (a.EvaluatorName ?? '').localeCompare(b.EvaluatorName ?? ''),
  };

  const monthCol = {
    title: 'Month',
    dataIndex: 'Month',
    key: 'Month',
    width: 140,
    sorter: (a, b) => (a.MonthKey ?? '').localeCompare(b.MonthKey ?? ''),
  };

  const columns = (viewMode === 'month'
    ? [evaluatorCol, monthCol, ...METRIC_COLS]
    : [evaluatorCol, ...METRIC_COLS]
  ).map(toResizable);

  const tableRowKey = viewMode === 'month'
    ? (r) => `${r.EvaluatorName}|${r.MonthKey}`
    : 'EvaluatorName';

  // ── Summary row ───────────────────────────────────────────────────────────
  const tableSummary = () => {
    const totPending   = rows.reduce((s, r) => s + (r.PendingCount   ?? 0), 0);
    const totDisputed  = rows.reduce((s, r) => s + (r.DisputedCount  ?? 0), 0);
    const totDone      = rows.reduce((s, r) => s + (r.CompletedCount ?? 0), 0);
    const totErrors    = rows.reduce((s, r) => s + (r.TotalErrors    ?? 0), 0);
    const totFeedbacks = rows.reduce((s, r) => s + (r.TotalFeedbacks ?? 0), 0);
    const bold = { fontWeight: 600 };
    const off  = viewMode === 'month' ? 1 : 0;
    return (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ background: '#fafafa' }}>
          <Table.Summary.Cell index={0} style={bold}>Total</Table.Summary.Cell>
          {viewMode === 'month' && <Table.Summary.Cell index={1} />}
          <Table.Summary.Cell index={1 + off} align="center" style={bold}>
            {totPending  > 0 ? totPending  : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2 + off} align="center" style={bold}>
            {totDisputed > 0 ? totDisputed : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3 + off} align="center" style={bold}>
            {totDone     > 0 ? totDone     : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4 + off} align="right" style={bold}>
            {totErrors > 0 ? <Tag color="red">{totErrors}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5 + off} align="right" style={bold}>
            {totFeedbacks > 0 ? <Tag color="orange">{totFeedbacks}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  // ── Pivot table (month mode only) ─────────────────────────────────────────
  const pivotMonths = viewMode === 'month'
    ? [...new Set(rows.map((r) => r.MonthKey))].sort()
    : [];

  const pivotMonthLabels = {};
  rows.forEach((r) => { if (r.MonthKey) pivotMonthLabels[r.MonthKey] = r.Month; });

  const pivotRows = viewMode === 'month' ? (() => {
    const byEval = {};
    rows.forEach((r) => {
      if (!byEval[r.EvaluatorName]) byEval[r.EvaluatorName] = { AssignedToUserID: r.AssignedToUserID };
      byEval[r.EvaluatorName][r.MonthKey] = {
        completed:  r.CompletedCount,
        errors:     r.TotalErrors,
        feedbacks:  r.TotalFeedbacks,
      };
    });
    return Object.keys(byEval).sort().map((name) => ({
      key: name,
      EvaluatorName: name,
      ...byEval[name],
    }));
  })() : [];

  const pivotColumns = viewMode === 'month' ? [
    { title: 'Evaluator', dataIndex: 'EvaluatorName', key: 'EvaluatorName', fixed: 'left', width: 180 },
    ...pivotMonths.map((mk) => ({
      title: pivotMonthLabels[mk] || mk,
      key: mk,
      width: 150,
      align: 'center',
      render: (_, record) => {
        const cell = record[mk];
        if (!cell) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return (
          <div style={{ lineHeight: 1.4 }}>
            <div style={{ fontWeight: 600, color: '#52c41a' }}>{cell.completed ?? 0}</div>
            <div style={{ fontSize: 11, color: cell.errors > 0 ? '#ff4d4f' : '#bfbfbf' }}>
              {cell.errors ?? 0} {cell.errors === 1 ? 'error' : 'errors'}
            </div>
            <div style={{ fontSize: 11, color: cell.feedbacks > 0 ? '#d46b08' : '#bfbfbf' }}>
              {cell.feedbacks ?? 0} {cell.feedbacks === 1 ? 'feedback' : 'feedbacks'}
            </div>
          </div>
        );
      },
    })),
  ] : [];

  // ── Export ─────────────────────────────────────────────────────────────────
  const exportToExcel = () => {
    const exportRows = rows.map((r) => {
      const base = { 'Evaluator': r.EvaluatorName };
      if (viewMode === 'month') base['Month'] = r.Month;
      base['Qty Submissions Pending']   = r.PendingCount;
      base['Qty Submissions Disputed']  = r.DisputedCount;
      base['Qty Submissions Completed'] = r.CompletedCount;
      base['Qty Section Errors']        = r.TotalErrors;
      base['Qty Section Feedbacks']     = r.TotalFeedbacks;
      return base;
    });
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Error Evaluator Stats');
    XLSX.writeFile(wb, 'error-evaluator-stats.xlsx');
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Error Evaluator Stats
        </Typography.Title>
        <Segmented
          value={viewMode}
          onChange={setViewMode}
          options={[
            { label: 'By Evaluator',     value: 'evaluator', icon: <TeamOutlined /> },
            { label: 'Month Over Month', value: 'month',     icon: <CalendarOutlined /> },
          ]}
        />
      </div>

      <Form
        form={form}
        layout="inline"
        onFinish={fetchStats}
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
      >
        <Form.Item name="dateRange" label="Date of Error">
          <RangePicker style={{ width: 280 }} />
        </Form.Item>

        <Form.Item name="form_id" label="Error Form">
          <Select
            allowClear
            placeholder="All forms"
            options={forms}
            style={{ width: 280 }}
          />
        </Form.Item>

        <Form.Item name="campaigns" label="Campaign">
          <Select
            mode="multiple"
            allowClear
            showSearch filterOption={selectFilter}
            placeholder="All campaigns"
            options={campaigns}
            style={{ width: 260 }}
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>Filter</Button>
            <Button onClick={() => { form.resetFields(); fetchStats(); }}>Reset</Button>
            <Button icon={<DownloadOutlined />} onClick={exportToExcel} disabled={rows.length === 0}>
              Export
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey={tableRowKey}
        dataSource={rows}
        columns={columns}
        components={{ header: { cell: ResizableTitle } }}
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} ${viewMode === 'month' ? 'rows' : 'evaluators'}` }}
        scroll={{ x: 'max-content' }}
        summary={rows.length > 0 ? tableSummary : undefined}
        onRow={(record) => ({
          onClick: () => openDrill(record),
          style: { cursor: 'pointer' },
        })}
      />

      {viewMode === 'month' && pivotRows.length > 0 && (
        <>
          <Typography.Title level={5} style={{ marginTop: 32, marginBottom: 12 }}>
            Month Over Month Pivot
          </Typography.Title>
          <Table
            rowKey="EvaluatorName"
            dataSource={pivotRows}
            columns={pivotColumns}
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            bordered
          />
        </>
      )}

      <Drawer
        title={drillTitle}
        open={drillOpen}
        onClose={() => setDrillOpen(false)}
        width={1000}
        destroyOnClose
      >
        <Table
          rowKey="ID"
          dataSource={drillRows}
          columns={DRILL_COLS(navigate)}
          loading={drillLoading}
          size="small"
          pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} submissions` }}
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onClick: () => navigate(`/errors/${record.ID}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Drawer>
    </>
  );
}
